import React, { useEffect, useMemo, useState } from 'react'
import api from 'api/api'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import { cacheAnswer, isViewerHydratedAnswerCacheEntry } from 'state/cache'
import QuoteLine from 'components/answers/QuoteLine'
import { quotePreviewForAnswer } from 'components/answers/quotePreview'
import { planQuoteNavigation } from 'lib/quoteMain'
import useStore from 'state/knovStore'
import useOpenRight from 'refactor/hooks/useOpenRight'
import styles from './sent-reply-quote.module.scss'

// Targets that already failed to fetch this session. Rows unmount/remount
// aggressively (visibility gating, virtualization, natural segments); without
// this, every scroll-past of a row quoting a deleted answer re-issues the
// doomed GET.
const failedTargetIds = new Set<string>()

interface SentReplyQuoteProps {
    targetAnswerId: string
    // Clicking goes to the quoted message: scrolls up to it and highlights it
    // when it is a row of this panel, opens its own thread to the right when
    // it lives elsewhere. Offered on active threads, notification cards and
    // Following-feed cards; other stream cards render the line inert.
    revealOnClick?: boolean
    // The rendered quest's head, so a branch guest quoting it scrolls within
    // this panel instead of navigating to the parent room.
    panelHeadAnswerId?: string | null
    panelHeadQuestId?: string | null
    // The quoting row is the branch head (the title) or is rendered on an
    // actionable stream card. In both cases the quoted post is not a row of
    // the current panel, so the click opens its thread to the right.
    fromPanelHead?: boolean
}

// The read side of the reply edge: a sent message's quote line, in the shared
// quote grammar. Renders above the message body; the caller owns adjacency
// suppression (this component never decides visibility).
const SentReplyQuote: React.FC<SentReplyQuoteProps> = ({
    targetAnswerId,
    revealOnClick,
    panelHeadAnswerId,
    panelHeadQuestId,
    fromPanelHead,
}) => {
    const { panel } = usePanelContext()
    const { openRight } = useOpenRight(panel?.panelId)
    const cachedTarget = useCachedAnswer(targetAnswerId ? String(targetAnswerId) : null)
    const targetIsHydrated = isViewerHydratedAnswerCacheEntry(cachedTarget)
    const [fetchFailed, setFetchFailed] = useState(() =>
        failedTargetIds.has(String(targetAnswerId)),
    )

    useEffect(() => {
        const key = String(targetAnswerId)
        setFetchFailed(failedTargetIds.has(key))
        // Compact quest payloads cache id-only answer stubs. They are enough
        // to render a row key, but not enough for the author/snippet here.
        if (!targetAnswerId || targetIsHydrated || failedTargetIds.has(key)) return

        let cancelled = false
        api.getAnswer(targetAnswerId)
            .then(answer => {
                if (!answer?.id) {
                    failedTargetIds.add(key)
                    if (!cancelled) setFetchFailed(true)
                    return
                }
                cacheAnswer(answer)
            })
            .catch(() => {
                failedTargetIds.add(key)
                if (!cancelled) setFetchFailed(true)
            })
        return () => {
            cancelled = true
        }
    }, [targetAnswerId, targetIsHydrated])

    // Action tags and media placeholders, not just words: a photo reply reads
    // as its photo. Recomputed only when the cached target changes.
    const preview = useMemo(() => quotePreviewForAnswer(cachedTarget), [cachedTarget])
    const unavailable =
        cachedTarget?.deleted || cachedTarget?.archive || (fetchFailed && !targetIsHydrated)

    // A title's quote leaves the panel for the target's own thread, so it can
    // only be pressed once the target's home is known — the fetch above
    // hydrates that; until then the line reads as loading, not as a button.
    const targetHomeKnown = !fromPanelHead || !!cachedTarget?.quest_id

    // Clicking (or pressing) the quote goes to the message it quotes, the same
    // way /quest/:questKey/answer/:answerId does: retarget this panel's filter
    // and let the route-owned machinery place, reveal and highlight the row.
    // Planned again at press time — the panel's filter can move between render
    // and press — and once here, for the pill's chevron.
    const planNavigation = () =>
        planQuoteNavigation({
            panel,
            targetAnswerId: String(targetAnswerId),
            targetQuestId: cachedTarget?.quest_id,
            panelHeadAnswerId,
            panelHeadQuestId,
            targetReplyToAnswerId: cachedTarget?.reply_to_answer_id,
            sourceIsPanelHead: !!fromPanelHead,
        })
    // Which way the press goes, drawn in the pill: a target that lives in
    // another thread opens to the right; a row of this panel is revealed above.
    const jumpDirection = planNavigation()?.kind === 'open-target-quest' ? 'right' : 'up'

    const onClick =
        revealOnClick && !unavailable && targetHomeKnown
            ? (ev: React.SyntheticEvent) => {
                  ev.preventDefault()
                  ev.stopPropagation()
                  const plan = planNavigation()
                  if (!plan) return

                  if (plan.kind === 'retarget-panel') {
                      // Two steps, the same pair a reused same-quest panel uses.
                      // The filter is what earns the deep-link support — the row
                      // is pinned against virtualization, a missing target is
                      // refetched, and the highlight backstop arms — while the
                      // reveal is called explicitly because QuestContainer's
                      // filter is a route prop and never sees this store write.
                      useStore.getState().panels.editPanel(plan.panelId, {
                          filter: { ...(panel?.filter || {}), answerId: plan.answerId },
                      })
                      void panel?.requestAnswerReveal?.(plan.answerId, {
                          questId: panel?.filter?.questId,
                          highlight: true,
                      })
                      return
                  }
                  if (plan.kind === 'open-target-quest') {
                      void openRight?.({ questId: plan.questId, answerId: plan.answerId })
                      return
                  }
                  // Already parked on this answer — just reveal again.
                  void panel?.requestAnswerReveal?.(plan.answerId, {
                      questId: panel?.filter?.questId,
                      highlight: true,
                  })
              }
            : undefined

    return (
        <div className={styles.sentReplyQuoteRow} data-testid="sent-reply-quote">
            <QuoteLine
                authorName={cachedTarget?.user?.name}
                preview={preview}
                unavailable={unavailable}
                onClick={onClick}
                jumpDirection={jumpDirection}
            />
        </div>
    )
}

export default SentReplyQuote
